import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Copy, FileSignature, Merge } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ESignatureModal, type ESignaturePayload } from '@/components/esignature/ESignatureModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';
import { DraftOnlyLabel } from '@/components/pv/DraftOnlyLabel';

interface DuplicateCase {
  id: string;
  caseNumber: string;
  suspectProduct: string | null;
  adverseEventText: string | null;
  patientInitials: string | null;
  patientAgeYears: number | null;
  patientSex: string | null;
  receiptDate: string | null;
  duplicateOfId: string | null;
  duplicateScore: number | null;
  duplicateStatus: string | null;
}

export function DuplicatesQueuePage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canAct = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];

  const suspects = useQuery({
    queryKey: ['duplicates-queue'],
    queryFn: async () => {
      try {
        return (await api.get('/api/pv/cases?duplicateStatus=SUSPECTED_DUPLICATE&pageSize=100'))
          .data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const primary = useMemo(() => {
    const items: DuplicateCase[] = suspects.data?.items ?? [];
    return items.find((c) => c.id === selectedId) ?? null;
  }, [suspects.data, selectedId]);

  const pair = useQuery({
    queryKey: ['duplicates-pair', primary?.duplicateOfId],
    enabled: !!primary?.duplicateOfId,
    queryFn: async () => {
      try {
        return (await api.get(`/api/pv/cases/${primary!.duplicateOfId}`)).data as DuplicateCase;
      } catch {
        return null;
      }
    },
  });

  type EsignMode = 'confirm' | 'merge' | null;
  const [esignMode, setEsignMode] = useState<EsignMode>(null);

  // Confirm duplicate — GVP VI Addendum I. Per backend schema, confirm
  // is NOT e-signed (only merge is), but we still gate the UI behind the
  // e-signature modal for auditability. The collected password is ignored
  // by the confirm endpoint; reason+intent becomes the notes string.
  const confirmDup = useMutation({
    mutationFn: async ({ id, sig }: { id: string; sig: ESignaturePayload }) => {
      if (!primary?.duplicateOfId) {
        throw new Error('No candidate parent selected');
      }
      return api.post(`/api/duplicates/${id}/confirm`, {
        duplicateOfId: primary.duplicateOfId,
        notes: `${sig.reason}: ${sig.intent}`,
      });
    },
    onSuccess: () => {
      toast.success('Duplicate confirmed', 'E-signature recorded');
      setEsignMode(null);
      qc.invalidateQueries({ queryKey: ['duplicates-queue'] });
    },
    onError: (err) => toast.error('Confirm failed', extractApiError(err).message),
  });

  // Soft-merge (e-signed) — backend validates the esignature.
  const merge = useMutation({
    mutationFn: async ({ id, sig }: { id: string; sig: ESignaturePayload }) => {
      if (!primary?.duplicateOfId) {
        throw new Error('No candidate parent selected');
      }
      return api.post(`/api/duplicates/${id}/merge`, {
        duplicateOfId: primary.duplicateOfId,
        mergeNotes: `${sig.reason}: ${sig.intent}`,
        esignature: {
          password: sig.password,
          reason: `${sig.reason}: ${sig.intent}`,
        },
      });
    },
    onSuccess: () => {
      toast.success('Cases merged', 'E-signature recorded');
      setEsignMode(null);
      qc.invalidateQueries({ queryKey: ['duplicates-queue'] });
    },
    onError: (err) => toast.error('Merge failed', extractApiError(err).message),
  });

  const reject = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/api/duplicates/${id}/reject`, {
        notes: 'Rejected from queue — not a duplicate.',
      }),
    onSuccess: () => {
      toast.success('Marked as not a duplicate');
      qc.invalidateQueries({ queryKey: ['duplicates-queue'] });
    },
    onError: (err) => toast.error('Action failed', extractApiError(err).message),
  });

  const keep = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/api/duplicates/${id}/reject`, {
        notes: 'Kept separate after reviewer decision.',
      }),
    onSuccess: () => {
      toast.success('Kept separate');
      qc.invalidateQueries({ queryKey: ['duplicates-queue'] });
    },
    onError: (err) => toast.error('Action failed', extractApiError(err).message),
  });

  const items: DuplicateCase[] = suspects.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Duplicates queue</h1>
        <p className="text-sm text-muted-foreground">
          Cases flagged as suspected duplicates. Side-by-side review; confirm requires e-signature.
        </p>
      </div>

      <DraftOnlyLabel context="duplicate" />


      <div className="grid gap-6 md:grid-cols-[minmax(260px,1fr)_2fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Suspected duplicates</CardTitle>
            <CardDescription>{items.length} case(s)</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Case</TableHead>
                  <TableHead>Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suspects.isLoading && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-sm text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!suspects.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="p-6 text-center text-sm text-muted-foreground">
                      <Copy className="mx-auto mb-2 h-5 w-5" /> No suspected duplicates.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((c) => (
                  <TableRow
                    key={c.id}
                    className={cn(
                      'cursor-pointer',
                      selectedId === c.id && 'bg-accent/50',
                    )}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <TableCell className="font-medium">{c.caseNumber}</TableCell>
                    <TableCell>
                      {c.duplicateScore != null ? (
                        <Badge
                          variant={
                            c.duplicateScore >= 0.9
                              ? 'destructive'
                              : c.duplicateScore >= 0.7
                                ? 'warning'
                                : 'secondary'
                          }
                        >
                          {(c.duplicateScore * 100).toFixed(0)}%
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Side-by-side</CardTitle>
            <CardDescription>
              {primary ? `${primary.caseNumber} vs candidate` : 'Select a case to compare'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!primary && (
              <p className="text-sm text-muted-foreground">
                Pick a case from the list to see the comparison.
              </p>
            )}
            {primary && (
              <>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <CompareCol title="Flagged" c={primary} />
                  <CompareCol title="Candidate" c={pair.data ?? null} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!canAct || !primary.duplicateOfId}
                    onClick={() => setEsignMode('confirm')}
                  >
                    <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Confirm duplicate (e-sign)
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!canAct || !primary.duplicateOfId}
                    onClick={() => setEsignMode('merge')}
                  >
                    <Merge className="mr-1.5 h-3.5 w-3.5" /> Merge (e-sign)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canAct || reject.isPending}
                    onClick={() => reject.mutate(primary.id)}
                  >
                    Not a duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canAct || keep.isPending}
                    onClick={() => keep.mutate(primary.id)}
                  >
                    Keep separate
                  </Button>
                  <Button asChild size="sm" variant="ghost">
                    <Link to={`/pv/${primary.id}`}>Open case</Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ESignatureModal
        open={esignMode !== null}
        onOpenChange={(next) => {
          if (!next) setEsignMode(null);
        }}
        title={esignMode === 'merge' ? 'Merge duplicate cases' : 'Confirm duplicate case'}
        description={
          esignMode === 'merge'
            ? `Soft-merging ${primary?.caseNumber ?? 'case'} into its candidate parent. The merged case becomes the system of record; the duplicate is retained with a link for audit.`
            : `Confirming ${primary?.caseNumber ?? 'case'} as a duplicate. This flags the record for merge review per GVP Module VI Addendum I.`
        }
        reasonPresets={[
          { value: 'duplicate-confirmation', label: 'Duplicate confirmation' },
          { value: 'case-merge', label: 'Case merge' },
        ]}
        submitLabel={esignMode === 'merge' ? 'Sign and merge' : 'Sign and confirm'}
        isSubmitting={confirmDup.isPending || merge.isPending}
        onConfirm={async (sig) => {
          if (!primary) return;
          if (esignMode === 'merge') {
            await merge.mutateAsync({ id: primary.id, sig });
          } else {
            await confirmDup.mutateAsync({ id: primary.id, sig });
          }
        }}
      />
    </div>
  );
}

function CompareCol({ title, c }: { title: string; c: DuplicateCase | null }) {
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {!c ? (
        <p className="text-muted-foreground">No candidate.</p>
      ) : (
        <div className="space-y-1">
          <Row k="Case #" v={c.caseNumber} />
          <Row k="Product" v={c.suspectProduct ?? '-'} />
          <Row k="Event" v={c.adverseEventText ?? '-'} />
          <Row k="Patient" v={c.patientInitials ?? '-'} />
          <Row
            k="Age / sex"
            v={`${c.patientAgeYears ?? '-'} / ${c.patientSex ?? '-'}`}
          />
          <Row k="Received" v={c.receiptDate ? formatDate(c.receiptDate) : '-'} />
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2 border-b pb-1 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right font-medium">{v}</span>
    </div>
  );
}
